import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
  OnDestroy,
  signal,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { ManageBriefTabsService } from '../../../../service/manage-brief-tabs/manage-brief-tabs.service';
import { BriefService } from '@/app/services/brief/brief.service';
import { ToastNotificationService } from '@/app/atoms/toast-notification/toast-notification.service';
import { SubmittedPostVersionEnum } from '@/app/common/general-types/briefs.dto';

@Component({
  selector: 'mbt-results-version-select',
  templateUrl: './results-version-select.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ResultsVersionSelect implements OnDestroy {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly briefService = inject(BriefService);
  private readonly toastNotificationService = inject(ToastNotificationService);
  readonly manageBriefTabsService = inject(ManageBriefTabsService);

  private readonly subscriptionList = new Subscription();

  saving = signal(false);

  readonly versionOptions = [
    {
      value: SubmittedPostVersionEnum.SuperSocial,
      label: 'Super Social',
      description: 'Track post results with Super Social.',
    },
    {
      value: SubmittedPostVersionEnum.UltraSocial,
      label: 'Ultra Social',
      description: 'Track post results with Ultra Social.',
    },
  ] as const;

  readonly currentVersion = computed(
    () => this.manageBriefTabsService.focussedBrief()?.shoutBrief?.resultsVersion,
  );

  readonly currentLabel = computed(() => this.labelFor(this.currentVersion()));

  readonly selectDisabledClass = computed(() =>
    this.saving() ? 'pointer-events-none opacity-50' : '',
  );

  private refreshView(): void {
    this.cdr.detectChanges();
  }

  labelFor(version: SubmittedPostVersionEnum | undefined): string {
    switch (version) {
      case SubmittedPostVersionEnum.SuperSocial:
        return 'Super Social';
      case SubmittedPostVersionEnum.UltraSocial:
        return 'Ultra Social';
      case SubmittedPostVersionEnum.Legacy:
        return 'Legacy';
      case SubmittedPostVersionEnum.Phyllo:
        return 'Phyllo';
      default:
        return version ?? 'Unknown';
    }
  }

  selectResultsVersion(version: SubmittedPostVersionEnum): void {
    const brief = this.manageBriefTabsService.focussedBrief();
    if (!brief || this.saving()) {
      return;
    }
    if (brief.shoutBrief?.resultsVersion === version) {
      return;
    }

    this.saving.set(true);
    this.refreshView();

    this.subscriptionList.add(
      this.briefService.saveResultsVersion(brief.id, version).subscribe({
        next: (response) => {
          this.manageBriefTabsService.focussedBrief.set(response);
          this.briefService.focussedBriefSignal.set(response);
          this.briefService.currentBriefSignal.set(response);
          this.saving.set(false);
          this.refreshView();
          this.toastNotificationService.alert({
            title: 'Results version updated',
            message: `Results version set to ${this.labelFor(version)}.`,
            type: 'success',
            icon: 'success',
          });
        },
        error: (err) => {
          console.error('ResultsVersionSelect.selectResultsVersion ERROR: ', err);
          this.saving.set(false);
          this.refreshView();
          this.toastNotificationService.alert({
            title: 'Error',
            message: 'Could not update results version. Please try again.',
            type: 'error',
            icon: 'error',
          });
        },
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptionList.unsubscribe();
  }
}
